import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const Home: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <section 
        className="hero-section" 
        style={{
          backgroundImage: `url('/images/hero-boiler-plant.jpeg')`
        }}
      >
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <div className="row">
            <div className="col-lg-8 col-xl-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <p className="text-uppercase text-primary fw-semibold mb-2" style={{ letterSpacing: '0.1em', fontSize: '0.85rem' }}>Sustainability Pros</p>
                <h1 className="display-4 fw-bold mb-3">We assess how your building really performs.<br /><span className="highlight-text">Then help you run it better.</span></h1>
                <p className="lead mb-4">We assess the mechanical, electrical, and plumbing systems behind your energy bills and maintenance costs — then hand you a prioritized, data-backed roadmap to a more sustainable, lower-cost building. From field assessment to facility operations.</p>
                <div className="d-flex flex-wrap gap-2">
                  <Link to="/services" className="btn btn-primary btn-lg">
                    Our Services
                  </Link>
                  <Link to="/contact" className="btn btn-outline-light btn-lg">
                    Get a Quote
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Built by practitioners, <span className="text-primary">for the people running the building</span></h2>
              <p className="lead">Most CMMS platforms are built by accountants. We built MEP Tools for the facility operator who actually has to keep the systems running.</p>
            </div>
          </div>
          
          <div className="row g-4">
            <div className="col-md-6 col-lg-3">
              <div className="card h-100 p-4 text-center" data-aos="fade-up" data-aos-delay="100">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-award"></i>
                </div>
                <h4>Field-First Expertise</h4>
                <p className="mb-0">Our team brings deep hands-on experience in MEP systems — from duct leakage testing and airflow balancing on new construction to diagnosing failing equipment in aging facilities.</p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="card h-100 p-4 text-center" data-aos="fade-up" data-aos-delay="200">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-lightbulb"></i>
                </div>
                <h4>Living Building Documentation</h4>
                <p className="mb-0">We replace the thumb drive in the drawer with a live digital package — O&M manuals, sequences of operations, PM schedules, and condition data linked to every asset in your building.</p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="card h-100 p-4 text-center" data-aos="fade-up" data-aos-delay="300">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-recycle"></i>
                </div>
                <h4>Ongoing Partnership</h4>
                <p className="mb-0">We stay engaged as your building changes — tenant moves, equipment replacements, system upgrades. Your facility data stays current, not frozen at closeout.</p>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="card h-100 p-4 text-center" data-aos="fade-up" data-aos-delay="400">
                <div className="feature-icon mx-auto">
                  <i className="bi bi-tools"></i>
                </div>
                <h4>Efficiency &amp; Savings</h4>
                <p className="mb-0">Every assessment zeroes in on the deficiencies and inefficiencies driving up your energy use and operating costs — so you can act on the fixes with the biggest payback first.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Digital Building Handoff */}
      <section className="section bg-dark text-white">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">The <span className="text-primary">Digital Building Handoff</span></h2>
              <p className="lead mb-0">Every building gets handed off once. Most owners get a binder. Ours get a working system.</p>
            </div>
          </div>

          <div className="row g-4 align-items-stretch">
            <div className="col-lg-6" data-aos="fade-right">
              <div className="p-4 p-md-5 h-100 rounded-4" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <h4 className="mb-3"><i className="bi bi-archive me-2 text-muted"></i>The traditional handoff</h4>
                <p>At closeout, the contractor delivers manuals, warranties, as-builts, and commissioning reports — as paper binders or a thumb drive. It goes in a drawer. The information is frozen on day one, and every renovation and equipment swap after that makes it less true.</p>
                <p className="mb-0">When the one technician who knew the building leaves, the knowledge leaves with them — and the facility slides into reactive maintenance.</p>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <div className="p-4 p-md-5 h-100 rounded-4" style={{ background: 'rgba(76,175,80,0.15)', border: '1px solid rgba(76,175,80,0.5)' }}>
                <h4 className="mb-3"><i className="bi bi-buildings me-2 text-primary"></i>The digital building handoff</h4>
                <p>We deliver the building's information as a live operational system: every asset in a structured inventory, O&amp;M documentation linked to each piece of equipment, and a preventive maintenance schedule running from day one — pulled straight from the BIM model (COBie) at closeout, or rebuilt through assessment for existing buildings.</p>
                <p className="mb-0">And because we stay engaged, the data stays current as your building changes — instead of expiring in a drawer.</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-5">
            <Link to="/mep-tools#digital-building-handoff" className="btn btn-primary btn-lg">
              See How the Handoff Works <i className="bi bi-arrow-right ms-1"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row mb-5">
            <div className="col-lg-6">
              <h2 className="mb-3">Our <span className="text-primary">Services</span></h2>
              <p className="lead">We help owners and facility managers see how their buildings are really performing — and make them more efficient, more sustainable, and less expensive to operate.</p>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end align-items-center">
              <Link to="/services" className="btn btn-outline-primary">
                View All Services
              </Link>
            </div>
          </div>
          
          <div className="row g-4">
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100">
                <div className="p-1">
                  <img
                    src="/images/ahu-interior-inspection.jpeg"
                    alt="Interior inspection of an air handling unit during a facility assessment"
                    className="img-fluid rounded"
                  />
                </div>
                <div className="card-body">
                  <h4 className="card-title">Existing Building Assessment</h4>
                  <p className="card-text">On-site evaluation of mechanical, electrical, and plumbing systems — documenting equipment condition, identifying deficiencies, and providing prioritized action items for repairs and capital planning.</p>
                  <Link to="/services" className="btn btn-sm btn-outline-primary">
                    Learn More <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100">
                <div className="p-1">
                  <img 
                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=600&q=80" 
                    alt="Data Analysis Dashboard" 
                    className="img-fluid rounded" 
                  />
                </div>
                <div className="card-body">
                  <h4 className="card-title">Energy &amp; Sustainability Analysis</h4>
                  <p className="card-text">Boiler and equipment efficiency modeling, energy conservation measures, and ROI analysis — quantifying where your building wastes energy and which upgrades pay for themselves.</p>
                  <Link to="/services" className="btn btn-sm btn-outline-primary">
                    Learn More <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="300">
              <div className="card h-100">
                <div className="p-1">
                  <img 
                    src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=600&q=80" 
                    alt="Sustainability Monitoring Dashboard" 
                    className="img-fluid rounded" 
                  />
                </div>
                <div className="card-body">
                  <h4 className="card-title">Design Document Review</h4>
                  <p className="card-text">Independent review of mechanical, electrical, and plumbing drawings, specifications, and sequences of operation to ensure design intent is captured before construction begins.</p>
                  <Link to="/services" className="btn btn-sm btn-outline-primary">
                    Learn More <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Preview Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row mb-5">
            <div className="col-lg-6">
              <h2 className="mb-3">Featured <span className="text-primary">Case Studies</span></h2>
              <p className="lead">See how our assessments have helped owners and facility managers make informed, cost-saving decisions about their buildings.</p>
            </div>
            <div className="col-lg-6 d-flex justify-content-lg-end align-items-center">
              <Link to="/projects" className="btn btn-outline-primary">
                View All Projects
              </Link>
            </div>
          </div>
          
          <div className="row g-4">
            <div className="col-md-6 col-lg-4" data-aos="fade-up">
              <Link to="/projects" className="project-card d-block">
                <img
                  src="/images/fay-street-boiler-plant.jpeg"
                  alt="Boiler plant at the Fay Street administrative buildings in Durham, NC"
                  className="project-image"
                />
                <div className="project-overlay">
                  <h5>Fay Street Administrative Buildings — Durham, NC</h5>
                  <p className="mb-0">Three-building mechanical and plumbing assessment covering boilers, pumps, air handlers, and building automation systems. Delivered prioritized action items to guide maintenance and capital planning.</p>
                </div>
              </Link>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <Link to="/projects" className="project-card d-block">
                <img 
                  src="/images/pump-motor-verification.jpeg"
                  alt="Pump motor inspection during a mechanical systems assessment"
                  className="project-image"
                />
                <div className="project-overlay">
                  <h5>Mechanical Systems Assessment — Durham, NC</h5>
                  <p className="mb-0">Field evaluation of pumps, motors, and heating equipment with energy conservation modeling — quantifying fuel savings and payback to guide the owner's capital plan.</p>
                </div>
              </Link>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <Link to="/projects" className="project-card d-block">
                <img 
                  src="/images/pressure-gauge-instrumentation.jpeg"
                  alt="Pressure gauge instrumentation documented during a controls assessment"
                  className="project-image" 
                />
                <div className="project-overlay">
                  <h5>BAS & Controls Assessment</h5>
                  <p className="mb-0">Evaluation of building automation system health and sequences of operation, identifying gaps between intended and actual system behavior and recommending corrective measures.</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Highway to Sustainability Overview */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6" data-aos="fade-right">
              <h2 className="mb-4">Our <span className="text-primary">Assessment Process</span></h2>
              <p className="lead mb-4">A structured approach that turns a walkthrough into a clear, prioritized plan for a more efficient and sustainable building — with the numbers to back every recommendation.</p>
              <div className="d-flex mb-4">
                <div className="me-3">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>1</div>
                </div>
                <div>
                  <h5 className="text-primary mb-1">Scope & Walkthrough</h5>
                  <p className="mb-0">Meet with facility staff to understand the building, its problem areas, and your goals — then define exactly what the assessment will cover</p>
                </div>
              </div>
              <div className="d-flex mb-4">
                <div className="me-3">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>2</div>
                </div>
                <div>
                  <h5 className="text-primary mb-1">Field Assessment & Data Capture</h5>
                  <p className="mb-0">On-site evaluation of mechanical, electrical, and plumbing systems — documenting equipment condition, performance, and energy use with photos and field data</p>
                </div>
              </div>
              <div className="d-flex mb-4">
                <div className="me-3">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>3</div>
                </div>
                <div>
                  <h5 className="text-primary mb-1">Condition & Energy Analysis</h5>
                  <p className="mb-0">Score asset condition, model energy conservation measures, and calculate the savings and payback behind each opportunity</p>
                </div>
              </div>
              <div className="d-flex mb-4">
                <div className="me-3">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>4</div>
                </div>
                <div>
                  <h5 className="text-primary mb-1">Prioritized Roadmap</h5>
                  <p className="mb-0">A written report separating immediate fixes from near-term repairs and capital planning — ranked by impact on cost, reliability, and sustainability</p>
                </div>
              </div>
              <div className="d-flex mb-4">
                <div className="me-3">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', fontSize: '1.2rem' }}>5</div>
                </div>
                <div>
                  <h5 className="text-primary mb-1">Living Documentation</h5>
                  <p className="mb-0">Findings carry forward as a live asset inventory and PM schedule in MEP Tools — so the report keeps working long after the engagement ends</p>
                </div>
              </div>
              <Link to="/services" className="btn btn-primary">
                Learn More About Our Services <i className="bi bi-arrow-right ms-1"></i>
              </Link>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <img
                src="/images/fieldwork-pump-gauges.jpeg"
                alt="Technician checking a circulation pump and pressure gauges in the field"
                className="img-fluid rounded-4 shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section bg-dark text-white">
        <div className="container">
          <div className="row g-4">
            <div className="col-md-6 col-lg-3">
              <div className="stat-card" data-aos="fade-up">
                <div className="stat-number">3</div>
                <div className="stat-title">Core Team Members</div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="stat-card" data-aos="fade-up" data-aos-delay="100">
                <div className="stat-number">3</div>
                <div className="stat-title">Project Phases Covered</div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="stat-card" data-aos="fade-up" data-aos-delay="200">
                <div className="stat-number">15+</div>
                <div className="stat-title">Years Experience</div>
              </div>
            </div>
            
            <div className="col-md-6 col-lg-3">
              <div className="stat-card" data-aos="fade-up" data-aos-delay="300">
                <div className="stat-number">100%</div>
                <div className="stat-title">Owner-Side Representation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEP Tools Teaser Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <h2 className="mb-4">Introducing <span className="text-primary">MEP Tools</span></h2>
              <p className="lead mb-4">A cloud-native SaaS platform built for MEP field professionals and facility managers — combining precision field calculations, facility condition assessment, and energy analysis in one subscription.</p>
              <ul className="list-unstyled mb-4">
                <li className="mb-3 d-flex">
                  <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                  <span><strong>Duct Air Leakage Testing (DALT)</strong> — SMACNA-compliant leakage calculations in the field</span>
                </li>
                <li className="mb-3 d-flex">
                  <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                  <span><strong>Ebtron Airflow Calibration</strong> — precise airflow measurement and verification</span>
                </li>
                <li className="mb-3 d-flex">
                  <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                  <span><strong>Boiler ECM Calculator</strong> — energy conservation measure analysis with ROI modeling</span>
                </li>
                <li className="mb-3 d-flex">
                  <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                  <span><strong>Facility Condition Assessment</strong> — weighted scoring and building-level rating system</span>
                </li>
                <li className="mb-3 d-flex">
                  <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                  <span><strong>Three-Phase Power Calculator</strong> — electrical load calculations for MEP field work</span>
                </li>
              </ul>
              <div className="d-flex flex-wrap gap-2">
                <Link to="/mep-tools" className="btn btn-primary">
                  Learn More <i className="bi bi-arrow-right ms-1"></i>
                </Link>
                <Link to="/contact" className="btn btn-outline-primary">
                  Request a Demo
                </Link>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <div className="bg-dark rounded-4 p-4 p-md-5 text-white">
                <div className="d-flex align-items-center mb-4">
                  <i className="bi bi-phone-fill text-primary me-3" style={{ fontSize: '2rem' }}></i>
                  <div>
                    <h5 className="mb-0">Mobile-First. Cloud-Native.</h5>
                    <p className="mb-0 text-muted">Works on any device, in the field, in real time.</p>
                  </div>
                </div>
                <hr className="border-secondary" />
                <div className="row g-4 text-center mt-2">
                  <div className="col-6">
                    <div className="p-3 rounded-3 bg-primary bg-opacity-25">
                      <i className="bi bi-wind text-primary mb-2" style={{ fontSize: '1.5rem' }}></i>
                      <div className="fw-semibold">DALT</div>
                      <small className="text-muted">Duct Leakage</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 rounded-3 bg-primary bg-opacity-25">
                      <i className="bi bi-speedometer2 text-primary mb-2" style={{ fontSize: '1.5rem' }}></i>
                      <div className="fw-semibold">Airflow</div>
                      <small className="text-muted">Ebtron Calibration</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 rounded-3 bg-primary bg-opacity-25">
                      <i className="bi bi-fire text-primary mb-2" style={{ fontSize: '1.5rem' }}></i>
                      <div className="fw-semibold">Boiler ECM</div>
                      <small className="text-muted">Energy Savings</small>
                    </div>
                  </div>
                  <div className="col-6">
                    <div className="p-3 rounded-3 bg-primary bg-opacity-25">
                      <i className="bi bi-building-check text-primary mb-2" style={{ fontSize: '1.5rem' }}></i>
                      <div className="fw-semibold">Assessment</div>
                      <small className="text-muted">Condition Scoring</small>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-4">
                  <span className="badge bg-primary px-3 py-2">Per-building subscription pricing</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary text-white text-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="mb-4">Ready to find out how your building is really performing?</h2>
              <p className="lead mb-4">Contact us for an assessment that shows exactly where your facility is losing energy and money — and what it takes to fix it.</p>
              <Link to="/contact" className="btn btn-light btn-lg">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;